import { useEffect } from 'react';

/*
 * Third-party marketing scripts (GTM, Hotjar, Intercom, Loamly, TikTok).
 * Loaded lazily in production only, and never when Do-Not-Track / GPC is set.
 * Event names and payloads are unchanged from the previous frontend so
 * existing dashboards keep working.
 *
 * Don't rename this file to analytics.js: EasyPrivacy blocks "/lib/analytics.js",
 * and in dev (one URL per module) that blanks the whole app for ad-blocker users.
 */

const scriptPromises = new Map();

const isDoNotTrackEnabled = () => {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  return (
    navigator.doNotTrack === '1' ||
    window.doNotTrack === '1' ||
    navigator.globalPrivacyControl === true
  );
};

export const whenIdle = (cb, timeout = 2000) => {
  if (typeof window === 'undefined') return undefined;
  if ('requestIdleCallback' in window) {
    return window.requestIdleCallback(cb, { timeout });
  }
  return window.setTimeout(cb, timeout);
};

export const shouldLoadThirdParty = () => {
  if (typeof window === 'undefined') return false;

  const override = import.meta.env.VITE_THIRD_PARTY_ENABLED;
  if (override === 'true') return true;
  if (override === 'false') return false;

  if (import.meta.env.MODE !== 'production') return false;
  if (isDoNotTrackEnabled()) return false;

  return true;
};

export const loadScriptOnce = ({ id, src }) => {
  if (typeof document === 'undefined' || !src) return Promise.resolve(null);
  if (id && scriptPromises.has(id)) return scriptPromises.get(id);

  const existing = id ? document.getElementById(id) : null;
  if (existing) return Promise.resolve(existing);

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    if (id) script.id = id;
    script.src = src;
    script.async = true;
    script.onload = () => resolve(script);
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });

  if (id) scriptPromises.set(id, promise);
  return promise;
};

const ensureDataLayer = () => {
  if (typeof window === 'undefined') return [];
  window.dataLayer = window.dataLayer || [];
  return window.dataLayer;
};

export const pushDataLayer = (payload) => {
  try {
    ensureDataLayer().push(payload);
  } catch {
    // no-op
  }
};

const loadGtm = (containerId = 'GTM-MTSCBWND') => {
  ensureDataLayer();
  pushDataLayer({ 'gtm.start': Date.now(), event: 'gtm.js' });
  return loadScriptOnce({
    id: 'gtm-script',
    src: `https://www.googletagmanager.com/gtm.js?id=${containerId}`,
  });
};

const loadHotjar = (hjid = 3603166, hjsv = 6) => {
  window.hj =
    window.hj ||
    function hj() {
      (window.hj.q = window.hj.q || []).push(arguments);
    };
  window._hjSettings = { hjid, hjsv };
  return loadScriptOnce({
    id: 'hotjar-script',
    src: `https://static.hotjar.com/c/hotjar-${hjid}.js?sv=${hjsv}`,
  });
};

const loadIntercom = (appId = 'uvq05xr1', apiBase = 'https://api-iam.intercom.io') => {
  window.intercomSettings = { api_base: apiBase, app_id: appId };

  const existing = window.Intercom;
  if (typeof existing === 'function') {
    existing('reattach_activator');
    existing('update', window.intercomSettings);
    return Promise.resolve(existing);
  }

  const stub = function intercomStub() {
    stub.c(arguments);
  };
  stub.q = [];
  stub.c = (args) => stub.q.push(args);
  window.Intercom = stub;

  return loadScriptOnce({
    id: 'intercom-widget',
    src: `https://widget.intercom.io/widget/${appId}`,
  });
};

const loadLoamly = (domain = 'nappr.io') =>
  loadScriptOnce({
    id: 'loamly-script',
    src: `https://app.loamly.ai/t.js?d=${encodeURIComponent(domain)}`,
  });

export const initThirdParty = () => {
  if (!shouldLoadThirdParty() || typeof document === 'undefined') return;

  const start = () => {
    const swallow = (p) => p?.catch?.(() => {});
    whenIdle(() => swallow(loadGtm()), 2000);
    whenIdle(() => swallow(loadHotjar()), 4000);
    whenIdle(() => swallow(loadIntercom()), 6000);
    whenIdle(() => swallow(loadLoamly()), 8000);
  };

  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });
};

/* ---------- TikTok pixel ---------- */

const TIKTOK_PIXEL_ID = import.meta.env.VITE_TIKTOK_PIXEL_ID || 'CJRO313C77U5E7966T8G';
let tiktokInit = null;

const initTikTok = () => {
  if (!shouldLoadThirdParty()) return Promise.resolve(null);
  if (tiktokInit) return tiktokInit;

  tiktokInit = import('tiktok-pixel')
    .then((mod) => {
      const pixel = mod.default || mod;
      pixel?.init?.(TIKTOK_PIXEL_ID);
      return pixel;
    })
    .catch(() => null);

  return tiktokInit;
};

export const trackTikTok = (eventName, payload, { defer = false } = {}) => {
  const run = () => {
    initTikTok().then((pixel) => {
      try {
        pixel?.track?.(eventName, payload);
      } catch {
        // no-op
      }
    });
  };
  if (defer) whenIdle(run, 2000);
  else run();
};

/** Fires the TikTok ViewContent event the old pages sent on mount. */
export function usePageView(contentName, category = 'page') {
  useEffect(() => {
    trackTikTok(
      'ViewContent',
      { content_name: contentName, content_category: category, timestamp: new Date() },
      { defer: true }
    );
  }, [contentName, category]);
}
